import axios from 'axios';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

class CPPDPdfGenerator {
  /**
   * Função para normalizar datas e corrigir bug de formato
   */
  normalizarData(data) {
    try {
      if (!data) return null;
      
      // Usar dayjs para consistência
      const dataObj = dayjs(data).utc();
      if (!dataObj.isValid()) {
        throw new Error('Data inválida');
      }
      
      return dataObj.format('YYYY-MM-DD');
    } catch (error) {
      console.error('Erro ao normalizar data:', error);
      return null;
    }
  }

  formatarDataParaExibicao(data) {
    try {
      if (!data) return 'Data não disponível';
      
      const dataObj = dayjs(data).utc();
      if (!dataObj.isValid()) return 'Data inválida';
      
      return dataObj.format('DD/MM/YYYY');
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data não disponível';
    }
  }
  
  /**
   * Função para formatar data para exibição (formato PT-BR)
   */
  formatarDataParaExibicao(data) {
    try {
      if (!data) return 'Data não disponível';
      
      const dataObj = new Date(data);
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      
      return dataObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Data não disponível';
    }
  }

  /**
   * Gera um relatório PDF do dashboard CPPD com os dados do backend
   * @param {string} clienteId - ID do cliente
   * @param {Object} filtros - Filtros para os dados (opcional)
   * @returns {Promise<Blob>} - Blob do PDF gerado
   */
  async gerarRelatorioDashboard(clienteId, filtros = {}) {
    try {
      const params = new URLSearchParams();
      params.append('cliente_id', clienteId);
      
      if (filtros.dataInicio) {
        const dataInicio = this.normalizarData(filtros.dataInicio);
        params.append('inicio', dataInicio);
      }
      
      if (filtros.dataFim) {
        const dataFim = this.normalizarData(filtros.dataFim);
        params.append('fim', dataFim);
      }
      
      if (filtros.membro_id) {
        params.append('membro_id', filtros.membro_id);
      }
      
      if (filtros.opcoes) {
        params.append('opcoes', JSON.stringify(filtros.opcoes));
      }
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4002';
      const apiUrl = `${baseUrl}/api`;
      const pdfUrl = `${apiUrl}/dashboard/governanca/cppd/pdf`;
      
      const response = await axios.get(`${pdfUrl}?${params.toString()}`, {
        headers: {
          ...headers,
          'Accept': 'application/json'
        }
      });
      
      const pdfBlob = await this.gerarPDF(response.data);
      return pdfBlob;
    } catch (error) {
      throw new Error('Falha ao gerar relatório PDF. ' + (error.response?.data?.message || error.message));
    }
  }
  
  /**
   * Gera um PDF com os dados recebidos do backend
   * @param {Object} dados - Dados recebidos do backend
   * @returns {Promise<Blob>} - Blob do PDF gerado
   */
  async gerarPDF(dados) {
    try {
      const doc = new jsPDF();
      
      const margin = 15;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const availableWidth = pageWidth - (margin * 2);
      
      const periodoInicioFormatado = this.formatarDataParaExibicao(dados.periodo.inicio);
      const periodoFimFormatado = this.formatarDataParaExibicao(dados.periodo.fim);
      
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Comitê de Privacidade e Proteção de Dados", pageWidth / 2, margin + 10, { align: "center" });
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Período: ${periodoInicioFormatado} a ${periodoFimFormatado}`, pageWidth / 2, margin + 20, { align: "center" });
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Cliente: ${dados.cliente.nome || 'Nome não disponível'}`, pageWidth / 2, margin + 30, { align: "center" });
      
      const agora = new Date();
      const dataAtual = agora.toLocaleDateString("pt-BR");
      const horaAtual = agora.toLocaleTimeString("pt-BR");
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Gerado em: ${dataAtual} - ${horaAtual}`, pageWidth - margin, margin + 40, { align: "right" });
      
      doc.setDrawColor(0, 0, 0);
      doc.line(margin, margin + 45, pageWidth - margin, margin + 45);
      
      let currentY = margin + 55;
      
      // 1. SUMÁRIO EXECUTIVO
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("1. SUMÁRIO EXECUTIVO", margin, currentY);
      currentY += 10;
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      
      const totalReunioes = dados.reunioes.total;
      const reunioesPendentes = dados.reunioes.pendentes;
      const reunioesConcluidas = dados.reunioes.concluidas;
      const proximasReunioes = dados.reunioes.proximas?.length || 0;
      const percentualExecucao = Math.round((reunioesConcluidas / (totalReunioes || 1)) * 100);
      const totalMembros = dados.membros.length;
      const membrosAtivos = dados.membros.filter(m => m.presente > 0).length;
      const taxaMediaParticipacao = dados.participacao.mediaTaxaParticipacao.toFixed(1);
      
      const textoSumario = 
        `Este relatório apresenta as atividades desenvolvidas pela Comissão Permanente de Pessoal Docente (CPPD) ` +
        `no período de ${periodoInicioFormatado} a ${periodoFimFormatado}. ` +
        `Durante este período, foram realizadas ${totalReunioes} reuniões, ` +
        `sendo ${reunioesConcluidas} concluídas (${percentualExecucao}% de execução) ` +
        `e ${reunioesPendentes} reuniões pendentes de finalização.

        A Comissão conta com ${totalMembros} membros, dos quais ${membrosAtivos} participaram ativamente das reuniões, ` +
        `com uma taxa média de participação de ${taxaMediaParticipacao}%. ` +
        `Há ${proximasReunioes} reuniões programadas para o próximo período.`;
      
      const splitSumario = doc.splitTextToSize(textoSumario, pageWidth - (margin * 2));
      doc.text(splitSumario, margin, currentY);
      currentY += splitSumario.length * 7 + 10;
      
      // 2. ESTATÍSTICAS GERAIS
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("2. ESTATÍSTICAS GERAIS", margin, currentY);
      currentY += 10;
      
      const estatisticasGerais = [
        ["Total de Reuniões", dados.reunioes.total.toString()],
        ["Reuniões Pendentes", dados.reunioes.pendentes.toString()],
        ["Próximas Reuniões", proximasReunioes.toString()],
        ["Reuniões Concluídas", dados.reunioes.concluidas.toString()],
        ["Total de Membros", dados.membros.length.toString()],
        ["Membros Ativos", membrosAtivos.toString()],
        ["Taxa Média de Participação", `${taxaMediaParticipacao}%`]
      ];
      
      doc.autoTable({
        startY: currentY,
        head: [["Informações", "Quantidade"]],
        body: estatisticasGerais,
        theme: 'grid',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: {
          cellPadding: 4,
          fontSize: 10
        },
        columnStyles: {
          0: { 
            cellWidth: availableWidth * 0.75,
            halign: 'left' 
          },
          1: { 
            cellWidth: availableWidth * 0.25,
            halign: 'center' 
          }
        },
        tableWidth: 'auto',
        margin: { left: margin, right: margin }
      });
      
      currentY = doc.lastAutoTable.finalY + 15;
      
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = margin;
      }
      
      // 3. ANÁLISE DE REUNIÕES
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("3. ANÁLISE DE REUNIÕES", margin, currentY);
      currentY += 10;
      
      // 3.1 Reuniões por Status
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("3.1 Reuniões por Status", margin, currentY);
      currentY += 10;
      
      const reunioesPorStatusCorrigido = [
        ["Reuniões Pendentes", dados.reunioes.pendentes.toString(), totalReunioes > 0 ? `${Math.round((dados.reunioes.pendentes / totalReunioes) * 100)}%` : "0%"],
        ["Próximas Reuniões", proximasReunioes.toString(), "N/A"],
        ["Reuniões Concluídas", dados.reunioes.concluidas.toString(), totalReunioes > 0 ? `${Math.round((dados.reunioes.concluidas / totalReunioes) * 100)}%` : "0%"]
      ];
      
      doc.autoTable({
        startY: currentY,
        head: [["Status", "Quantidade", "Percentual"]],
        body: reunioesPorStatusCorrigido,
        theme: 'grid',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: {
          cellPadding: 4,
          fontSize: 10
        },
        columnStyles: {
          0: { 
            cellWidth: availableWidth * 0.50,
            halign: 'left' 
          },
          1: { 
            cellWidth: availableWidth * 0.25,
            halign: 'center' 
          },
          2: { 
            cellWidth: availableWidth * 0.25,
            halign: 'center' 
          }
        },
        tableWidth: 'auto',
        margin: { left: margin, right: margin }
      });
      
      currentY = doc.lastAutoTable.finalY + 15;
      
      // 3.2 Próximas Reuniões
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("3.2 Próximas Reuniões", margin, currentY);
      currentY += 10;

      if (dados.reunioes.proximas && dados.reunioes.proximas.length > 0) {
        doc.autoTable({
          startY: currentY,
          head: [["Assunto", "Data", "Status", "Responsável"]],
          body: dados.reunioes.proximas.map(item => [
            item.nome,
            item.data,
            item.status,
            item.responsavel
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            cellPadding: 4,
            fontSize: 9,
            overflow: 'ellipsize'
          },
          columnStyles: {
            0: { cellWidth: availableWidth * 0.35 },
            1: { cellWidth: availableWidth * 0.20, halign: 'center', fontSize: 8 },
            2: { cellWidth: availableWidth * 0.15, halign: 'center', fontSize: 8 },
            3: { cellWidth: availableWidth * 0.30 }
          },
          tableWidth: 'auto',
          margin: { left: margin, right: margin }
        });
        
        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text("Não há reuniões agendadas para o próximo período.", margin, currentY);
        currentY += 10;
      }
      
      if (currentY > pageHeight - 120) {
        doc.addPage();
        currentY = margin;
      }
      
      // 3.3 Evolução de Reuniões
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("3.3 Evolução de Reuniões", margin, currentY);
      currentY += 10;
      
      if (dados.reunioes.evolucao && dados.reunioes.evolucao.length > 0) {
        doc.autoTable({
          startY: currentY,
          head: [["Período", "Quantidade"]],
          body: dados.reunioes.evolucao.map(item => [
            item.periodo,
            item.quantidade.toString()
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            cellPadding: 4,
            fontSize: 10
          },
          columnStyles: {
            0: { cellWidth: availableWidth * 0.70, halign: 'left' },
            1: { cellWidth: availableWidth * 0.30, halign: 'center' }
          },
          tableWidth: 'auto',
          margin: { left: margin, right: margin }
        });
        
        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text("Não há dados de evolução de reuniões disponíveis para o período.", margin, currentY);
        currentY += 15;
      }
      
      if (currentY > pageHeight - 120) {
        doc.addPage();
        currentY = margin;
      }
      
      // 4. MEMBROS DA COMISSÃO
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("4. MEMBROS DA COMISSÃO", margin, currentY);
      currentY += 10;
      
      // 4.1 Membros por Função
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("4.1 Membros por Função", margin, currentY);
      currentY += 10;
      
      if (dados.membros && dados.membros.length > 0) {
        doc.autoTable({
          startY: currentY,
          head: [["Nome", "Função", "Email"]],
          body: dados.membros.map(membro => [
            membro.nome || "Não identificado",
            membro.funcao || "Membro",
            membro.email || "-"
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            cellPadding: 4,
            fontSize: 9,
            overflow: 'ellipsize'
          },
          columnStyles: {
            0: { cellWidth: availableWidth * 0.45 },
            1: { cellWidth: availableWidth * 0.25 },
            2: { cellWidth: availableWidth * 0.30 }
          },
          tableWidth: 'auto',
          margin: { left: margin, right: margin }
        });
        
        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text("Não há membros registrados para a comissão.", margin, currentY);
        currentY += 15;
      }
      
      if (currentY > pageHeight - 120) {
        doc.addPage();
        currentY = margin;
      }
      
      // 4.2 Estatísticas de Participação por Membro
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("4.2 Estatísticas de Participação por Membro", margin, currentY);
      currentY += 10;
      
      if (dados.membros && dados.membros.length > 0) {
        doc.autoTable({
          startY: currentY,
          head: [["Nome", "Convidado", "Presente", "Taxa de Participação"]],
          body: dados.membros.map(membro => [
            membro.nome || "Não identificado",
            membro.convidado.toString(),
            membro.presente.toString(),
            `${membro.taxaParticipacao.toFixed(1)}%`
          ]),
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            cellPadding: 4,
            fontSize: 9,
            overflow: 'ellipsize'
          },
          columnStyles: {
            0: { cellWidth: availableWidth * 0.40, halign: 'left' },
            1: { cellWidth: availableWidth * 0.20, halign: 'center' },
            2: { cellWidth: availableWidth * 0.20, halign: 'center' },
            3: { cellWidth: availableWidth * 0.20, halign: 'center' }
          },
          tableWidth: 'auto',
          margin: { left: margin, right: margin }
        });
        
        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text("Não há dados de participação de membros disponíveis.", margin, currentY);
        currentY += 15;
      }
      
      if (currentY > pageHeight - 120) {
        doc.addPage();
        currentY = margin;
      }
      
      // 5. CONSIDERAÇÕES FINAIS
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("5. CONSIDERAÇÕES FINAIS", margin, currentY);
      currentY += 10;
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      
      const textoConsideracoes = 
        `Este relatório apresentou uma visão geral das atividades da Comissão Permanente de Pessoal Docente no período analisado. ` +
        `Foram realizadas ${reunioesConcluidas} reuniões de um total de ${totalReunioes} programadas, ` +
        `o que representa uma execução de ${percentualExecucao}%.

        A participação média dos membros foi de ${taxaMediaParticipacao}%, ` +
        `o que indica um nível ${parseFloat(taxaMediaParticipacao) > 70 ? 'satisfatório' : 'que pode ser melhorado'} ` +
        `de engajamento da comissão.

        Recomendações para o próximo período:
        1. ${reunioesPendentes > 0 ? `Finalizar as ${reunioesPendentes} reuniões pendentes.` : 'Manter o planejamento atualizado de reuniões.'}
        2. ${parseFloat(taxaMediaParticipacao) < 70 ? 'Desenvolver estratégias para aumentar a participação dos membros.' : 'Manter o bom nível de participação dos membros.'}
        3. Continuar a documentação e organização sistemática das reuniões e seus resultados.`;
      
      const splitConsideracoes = doc.splitTextToSize(textoConsideracoes, pageWidth - (margin * 2));
      doc.text(splitConsideracoes, margin, currentY);
      
      // Adicionar rodapé com numeração de páginas
      const totalPages = doc.internal.getNumberOfPages();
      
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
        
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Página ${i} de ${totalPages}`, 
          pageWidth / 2, 
          pageHeight - 10, 
          { align: "center" }
        );
        
        doc.text("Relatório CPPD", margin, pageHeight - 10);
        
        doc.text(
          `Relatório gerado em ${dataAtual}`, 
          pageWidth - margin, 
          pageHeight - 10, 
          { align: "right" }
        );
      }
      
      return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Gera um relatório e salva como arquivo PDF
   */
  async gerarEBaixar(clienteId, filtros = {}, filename = 'relatorio-cppd.pdf') {
    try {
      const pdfBlob = await this.gerarRelatorioDashboard(clienteId, filtros);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Gera um relatório e abre para visualização
   */
  async gerarEVisualizar(clienteId, filtros = {}) {
    try {
      const pdfBlob = await this.gerarRelatorioDashboard(clienteId, filtros);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      throw error;
    }
  }
}

export default new CPPDPdfGenerator();